/**
 * フォーム回収データへの返信メール編集モーダル (2026-07-23)。
 * 見積書/請求書の DocumentMailDialog と同じロジック・UIUX (書類PDFリンク欄のみ無し)。
 *
 * フロー:
 *  1. open 時に prepareFormReplyMail で初期値ロード
 *     (宛先=回答者メアド / 送信元=Deal カテゴリ別→既定 / 本文=空欄+会社フッタ自動付与)
 *  2. 送信元: mail_accounts(active) から選択
 *  3. CC: チップ入力。入力中に mail_contacts を背景検索しサジェスト
 *  4. 件名 / 本文: 編集可能
 *  5. 送信 → sendFormReplyMail が送信 + replySentAt 書込 (「返答待ち」→「返答済み」)
 */
import { Loader2, Mail, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label, Textarea } from '@/components/ui/input';
import {
  usePrepareFormReplyMail,
  useSendFormReplyMail,
} from '@/hooks/useForms';
import { useMailAccounts } from '@/hooks/useMailAccounts';
import { useMailContacts } from '@/hooks/useMailContacts';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  formId: string;
  submissionId: string | null;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function SubmissionReplyMailDialog({
  open,
  onOpenChange,
  formId,
  submissionId,
}: Props) {
  const { data: accounts } = useMailAccounts();
  const { data: contacts } = useMailContacts();

  const prepareMutation = usePrepareFormReplyMail();
  const sendMutation = useSendFormReplyMail();

  const [loading, setLoading] = useState(false);
  const [to, setTo] = useState('');
  const [fromAccountId, setFromAccountId] = useState('');
  const [ccList, setCcList] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [showSuggest, setShowSuggest] = useState(false);

  const activeAccounts = useMemo(
    () => (accounts ?? []).filter((a) => a.status === 'active'),
    [accounts],
  );

  // open 時に prepare で初期値ロード
  const loadedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!open || !submissionId) return;
    if (loadedRef.current === submissionId) return;
    loadedRef.current = submissionId;
    setLoading(true);
    prepareMutation
      .mutateAsync({ submissionId })
      .then((r) => {
        setTo(r.defaultTo ?? '');
        setFromAccountId(r.fromAccountId ?? '');
        setSubject(r.subject ?? '');
        setBody(r.body ?? '');
        setCcList([]);
        setCcInput('');
      })
      .catch((err) => {
        toast.error(`メール内容の取得に失敗しました: ${(err as Error).message}`);
        onOpenChange(false);
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, submissionId]);

  // close 時にロード済みフラグをリセット（次回開いたとき再取得）
  useEffect(() => {
    if (!open) loadedRef.current = null;
  }, [open]);

  // CC サジェスト候補（mail_contacts を背景検索）
  const suggestions = useMemo(() => {
    const q = ccInput.trim().toLowerCase();
    if (!q) return [];
    const seen = new Set(ccList.map((e) => e.toLowerCase()));
    const out: { email: string; name: string }[] = [];
    for (const c of contacts ?? []) {
      const emails = [
        c.defaultEmail,
        // c.emails は { address, addedAt } オブジェクトの配列なので address を取り出す
        ...(Array.isArray(c.emails) ? c.emails.map((e) => e.address) : []),
      ].filter((e): e is string => !!e);
      for (const email of emails) {
        const el = email.toLowerCase();
        if (seen.has(el)) continue;
        const name = c.name ?? '';
        if (el.includes(q) || name.toLowerCase().includes(q)) {
          out.push({ email, name });
          seen.add(el);
        }
      }
      if (out.length >= 8) break;
    }
    return out.slice(0, 8);
  }, [ccInput, contacts, ccList]);

  const addCc = (email: string) => {
    const e = email.trim();
    if (!e) return;
    if (!EMAIL_RE.test(e)) {
      toast.error(`メールアドレスの形式が正しくありません: ${e}`);
      return;
    }
    if (ccList.some((x) => x.toLowerCase() === e.toLowerCase())) {
      setCcInput('');
      return;
    }
    setCcList((prev) => [...prev, e]);
    setCcInput('');
    setShowSuggest(false);
  };

  const removeCc = (email: string) => {
    setCcList((prev) => prev.filter((x) => x !== email));
  };

  const handleSend = async () => {
    if (!submissionId) return;
    if (!to.trim()) {
      toast.error('宛先を入力してください');
      return;
    }
    // 未確定の CC 入力（Enter/カンマ未押下のまま送信ボタンを押した場合）も CC に含める。
    // これを行わないと、入力しただけの CC が黙って破棄され「CC が届かない」不具合になる。
    const pendingCc = ccInput.trim();
    let effectiveCc = ccList;
    if (pendingCc) {
      if (!EMAIL_RE.test(pendingCc)) {
        toast.error(`CC のメールアドレス形式が正しくありません: ${pendingCc}`);
        return;
      }
      if (!ccList.some((x) => x.toLowerCase() === pendingCc.toLowerCase())) {
        effectiveCc = [...ccList, pendingCc];
      }
    }
    try {
      const r = await sendMutation.mutateAsync({
        formId,
        submissionId,
        to: to.trim(),
        fromAccountId: fromAccountId || null,
        cc: effectiveCc,
        subject,
        body,
      });
      if (r.mailSent) {
        toast.success('返信メールを送信しました');
        onOpenChange(false);
      } else {
        toast.error(`送信できませんでした: ${r.reason ?? '理由不明'}`);
      }
    } catch (err) {
      toast.error(`送信失敗: ${(err as Error).message}`);
    }
  };

  const sending = sendMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>回答者へメール送信</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12 text-[var(--text-muted)]">
            <Loader2 className="size-5 animate-spin mr-2" />
            メール内容を読み込み中…
          </div>
        ) : (
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            {/* 送信元 */}
            <div>
              <Label className="text-xs">送信元</Label>
              <select
                value={fromAccountId}
                onChange={(e) => setFromAccountId(e.target.value)}
                className="flex h-9 w-full rounded-sm border border-[var(--border)] bg-[var(--bg-input)] px-3 text-[13px] text-[var(--text-primary)] focus:border-[var(--border-focus)] focus:outline-none"
              >
                {activeAccounts.length === 0 && (
                  <option value="">送信可能なアカウントがありません</option>
                )}
                {activeAccounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.displayName ? `${a.displayName} <${a.email}>` : a.email}
                  </option>
                ))}
              </select>
            </div>

            {/* 宛先 */}
            <div>
              <Label className="text-xs">宛先</Label>
              <Input
                type="email"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="customer@example.com"
              />
            </div>

            {/* CC */}
            <div className="relative">
              <Label className="text-xs">CC</Label>
              <div className="flex flex-wrap gap-1 rounded-sm border border-[var(--border)] bg-[var(--bg-input)] p-1.5 focus-within:border-[var(--border-focus)]">
                {ccList.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 rounded-sm bg-[var(--bg-elevated)] px-2 py-0.5 text-xs text-[var(--text-secondary)]"
                  >
                    {email}
                    <button
                      type="button"
                      onClick={() => removeCc(email)}
                      className="text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                    >
                      <X className="size-3" />
                    </button>
                  </span>
                ))}
                <input
                  value={ccInput}
                  onChange={(e) => {
                    setCcInput(e.target.value);
                    setShowSuggest(true);
                  }}
                  onFocus={() => setShowSuggest(true)}
                  onBlur={() => {
                    setTimeout(() => setShowSuggest(false), 150);
                    // フォーカスが外れたら、有効な未確定 CC 入力をチップ化（確定し忘れ対策）
                    const pending = ccInput.trim();
                    if (pending && EMAIL_RE.test(pending)) addCc(pending);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      addCc(ccInput);
                    } else if (
                      e.key === 'Backspace' &&
                      !ccInput &&
                      ccList.length > 0
                    ) {
                      removeCc(ccList[ccList.length - 1]!);
                    }
                  }}
                  placeholder={ccList.length === 0 ? 'CCを追加（入力で候補表示）' : ''}
                  className="flex-1 min-w-[140px] bg-transparent px-1 text-[13px] text-[var(--text-primary)] focus:outline-none"
                />
              </div>
              {showSuggest && suggestions.length > 0 && (
                <ul className="absolute z-50 mt-1 w-full max-h-48 overflow-y-auto rounded-sm border border-[var(--border)] bg-[var(--bg-surface)] shadow-lg">
                  {suggestions.map((s) => (
                    <li key={s.email}>
                      <button
                        type="button"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          addCc(s.email);
                        }}
                        className="flex w-full flex-col items-start px-3 py-1.5 text-left hover:bg-[var(--bg-hover)]"
                      >
                        <span className="text-[13px] text-[var(--text-primary)]">
                          {s.email}
                        </span>
                        {s.name && (
                          <span className="text-xs text-[var(--text-muted)]">
                            {s.name}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 件名 */}
            <div>
              <Label className="text-xs">件名</Label>
              <Input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </div>

            {/* 本文 */}
            <div>
              <Label className="text-xs">本文</Label>
              <Textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={12}
                className="font-mono text-xs"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={sending}
          >
            キャンセル
          </Button>
          <Button
            variant="primary"
            onClick={handleSend}
            disabled={loading || sending || !submissionId}
          >
            <Mail className="size-3.5" />
            {sending ? '送信中…' : '送信'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
